<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import LanguageSelector from '../components/LanguageSelector.vue'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

// Get token, email, and language from URL
const token = ref(route.query.token as string || '')
const email = ref(route.query.email as string || '')
const lang = route.query.lang as string

// Set language from URL parameter if provided
if (lang && ['pl', 'en', 'fr', 'es', 'de', 'it', 'pt', 'cs', 'nl', 'sv', 'da', 'hu', 'ro', 'no', 'lt', 'hr', 'fi'].includes(lang)) {
  locale.value = lang
}

// Form state
const password = ref('')
const passwordConfirmation = ref('')

// UI state
const loading = ref(false)
const validating = ref(true)
const tokenValid = ref(false)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

/**
 * Validate token on component mount
 */
const validateToken = async () => {
  if (!token.value || !email.value) {
    error.value = t('resetPassword.invalidLink')
    validating.value = false
    return
  }

  validating.value = true

  try {
    await api.get('v1/auth/reset-password/validate', {
      params: {
        token: token.value,
        email: email.value
      }
    })

    tokenValid.value = true
  } catch (err: any) {
    console.error('Token validation error:', err)

    if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else {
      error.value = t('resetPassword.invalidToken')
    }

    tokenValid.value = false
  } finally {
    validating.value = false
  }
}

/**
 * Handle reset password form submission
 */
const handleResetPassword = async () => {
  // Reset messages
  error.value = null
  successMessage.value = null
  validationErrors.value = {}
  loading.value = true

  try {
    const response = await api.post('v1/auth/reset-password', {
      token: token.value,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value
    })

    successMessage.value = response.data.message || t('resetPassword.successMessage')

    // Redirect to login after 3 seconds
    setTimeout(() => {
      router.push('/login')
    }, 3000)
  } catch (err: any) {
    console.error('Reset password error:', err)

    if (err.response?.data) {
      const apiError = err.response.data

      // Handle validation errors
      if (apiError.errors) {
        validationErrors.value = apiError.errors
      }

      // Handle general error message
      if (apiError.message) {
        error.value = apiError.message
      }
    } else {
      error.value = t('resetPassword.errorResetting')
    }
  } finally {
    loading.value = false
  }
}

/**
 * Get validation error for field
 */
const getFieldError = (field: string): string | null => {
  return validationErrors.value[field]?.[0] || null
}

// Validate token on mount
onMounted(() => {
  validateToken()
})
</script>

<template>
  <main class="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-50">
    <div class="w-full max-w-md">
      <!-- Language Selector -->
      <div class="mb-4 flex justify-end">
        <LanguageSelector />
      </div>

      <!-- Reset Password Card -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-md p-4">
        <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('resetPassword.title') }}</h2>

        <!-- Validating Token -->
        <div v-if="validating" class="py-8 text-center">
          <p class="text-sm text-gray-600">{{ t('resetPassword.validating') }}</p>
        </div>

        <!-- Token Invalid -->
        <div v-else-if="!tokenValid" class="space-y-4">
          <div class="p-3 bg-red-50 border-2 border-red-300 rounded">
            <p class="text-sm text-red-700">{{ error }}</p>
          </div>

          <div class="pt-3 border-t border-gray-200">
            <router-link to="/forgot-password" class="text-sm text-blue-600 hover:text-blue-700">
              {{ t('resetPassword.requestNewLink') }}
            </router-link>
          </div>
        </div>

        <!-- Reset Password Form -->
        <div v-else>
          <p class="text-sm text-gray-600 mb-4">{{ t('resetPassword.subtitle') }}</p>

          <!-- Error Alert -->
          <div v-if="error" class="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded">
            <p class="text-sm text-red-700">{{ error }}</p>
          </div>

          <!-- Success Alert -->
          <div v-if="successMessage" class="mb-4 p-3 bg-green-50 border-2 border-green-300 rounded">
            <p class="text-sm text-green-700">{{ successMessage }}</p>
            <p class="text-xs text-green-600 mt-1">{{ t('resetPassword.redirecting') }}</p>
          </div>

          <form v-if="!successMessage" @submit.prevent="handleResetPassword" class="space-y-3">
            <!-- Email (readonly) -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('resetPassword.email') }}</label>
              <input
                type="email"
                id="email"
                v-model="email"
                readonly
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 cursor-not-allowed"
              />
            </div>

            <!-- New Password -->
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ t('resetPassword.newPassword') }}</label>
              <input
                type="password"
                id="password"
                v-model="password"
                required
                minlength="8"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :class="{ 'border-red-500': getFieldError('password') }"
                :placeholder="t('resetPassword.passwordPlaceholder')"
              />
              <p v-if="getFieldError('password')" class="mt-1 text-sm text-red-600">
                {{ getFieldError('password') }}
              </p>
            </div>

            <!-- Confirm Password -->
            <div>
              <label for="password_confirmation" class="block text-sm font-medium text-gray-700 mb-1">{{ t('resetPassword.confirmPassword') }}</label>
              <input
                type="password"
                id="password_confirmation"
                v-model="passwordConfirmation"
                required
                minlength="8"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
                :placeholder="t('resetPassword.confirmPasswordPlaceholder')"
              />
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="loading"
              class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? t('resetPassword.resetting') : t('resetPassword.resetButton') }}
            </button>
          </form>

          <!-- Additional Links -->
          <div v-if="!successMessage" class="mt-4 pt-3 border-t border-gray-200">
            <router-link to="/login" class="text-sm text-blue-600 hover:text-blue-700">
              {{ t('resetPassword.backToLogin') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
